import { MouseEvent, useCallback, useEffect, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { TRPCMethodEnum, TRPCResourceEnum } from '../../../api/trpcApi/types';
import { useTRPCRequest } from '../../../hooks';
import { Title } from '../../../components/Title';
import { LoadingModalComponent } from '../../../components/modals';

import { OrderDetails, OrderItem } from '../types';
import DetailRow from '../components/DetailRow';

export const Order = () => {
  const navigate = useNavigate();
  const [orderDetails, setOrderDetails] = useState<OrderDetails | undefined>();
  const [orderItems, setOrderItems] = useState<OrderItem[] | undefined>();
  const [orderType, setOrderType] = useState<string>('shopify');
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const params = useParams();
  const { handleTRPCRequest } = useTRPCRequest();

  const fetchOrder = useCallback(async () => {
    setIsLoading(true);

    const response = await handleTRPCRequest({
      method: TRPCMethodEnum.get,
      resourceType: TRPCResourceEnum.orders,
      requestBody: {
        orderIdentifier: params.orderIdentifier
      }
    });
    if (response.error) {
      setIsLoading(false);
    }
    const { lineItems, order } = response.res;
    const orderIdentifier = order?.amazon_order_id ?? order?.shopify_order_name;
    const orderDetails = {
      clientFullName: [order.customer.first_name, order.customer.last_name]
        .filter(a => a)
        .join(' '),
      createdAt: order.created_at,
      clientEmail: order.customer.email,
      orderIdentifier: orderIdentifier
    };

    if (order?.amazon_order_id) {
      setOrderType('amazon');
    }

    setOrderDetails(orderDetails);
    setOrderItems(lineItems);
    setIsLoading(false);
  }, [handleTRPCRequest, params.orderIdentifier]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  const handlePdfPreview = useCallback((pdfPaths: string[]) => {
    return (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      pdfPaths.forEach(pdfPath => downloadPdf(pdfPath));
    };
  }, []);

  const handleEditOrderItem = useCallback(
    (cart_item_id: string) => {
      return () => {
        navigate(
          `/orders/${encodeURIComponent(
            params.orderIdentifier ?? ''
          )}/edit/${cart_item_id}`
        );
      };
    },
    [navigate, params.orderIdentifier]
  );

  const printDate = () => {
    if (orderDetails?.createdAt) {
      const date = new Date(orderDetails?.createdAt);
      const day = date.getDate();
      const month = date.getMonth() + 1;
      const year = date.getFullYear();
      const time = date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        hour12: true,
        minute: 'numeric'
      });

      return `${month}/${day}/${year} ${time}`;
    }
  };

  if (isLoading) {
    return <LoadingModalComponent isOpen={isLoading} />;
  }

  return (
    <>
      {orderDetails && (
        <div className="flex justify-between">
          <Title
            title={`${orderDetails.orderIdentifier} • ${printDate()} • ${
              orderDetails.clientFullName
            }`}
          />
          {orderDetails?.clientEmail ? (
            <div className="border-navy text-navy flex items-center rounded-full border-2 border-solid p-2">
              {orderDetails?.clientEmail}
            </div>
          ) : null}
        </div>
      )}

      {orderItems && (
        <DetailRow
          orderItems={orderItems}
          onEditClick={handleEditOrderItem}
          onPdfClick={handlePdfPreview}
          orderType={orderType}
        />
      )}
    </>
  );
};

async function downloadPdf(url: string) {
  const response = await fetch(url);
  const blob = await response.blob();
  const downloadUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = getFileName(url);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function getFileName(url: string) {
  const regex = /orders\/[^/]+\/(.*?)\.pdf/;
  const match = url.match(regex);
  return decodeURIComponent(match![1]) + '.pdf';
}
